import { useAuthenticator } from "@aws-amplify/ui-react";
import Dashboard from "assets/img/svg/dashboard";
import { DocumentOutline } from "assets/img/svg/documentOutline";
import DrawIcon from "assets/img/svg/drawIcon";
import FileSuccess from "assets/img/svg/fileSuccess";
import Folder from "assets/img/svg/folder";
import Beneficiaries from "assets/img/svg/navbar/beneficiaries";
import History from "assets/img/svg/navbar/history";
import Management from "assets/img/svg/navbar/management";
import Settings from "assets/img/svg/navbar/settings";
import Titles from "assets/img/svg/navbar/titles";
import Wallet from "assets/img/svg/navbar/wallet";
import MaintenanceSoonBanner from "components/MaintenanceSoonBanner";
import { PortalType } from "contexts/portal.context";
import { getCognitoUserAttribute } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useContext, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useAppSelector } from "store/store";
import { EPortalType } from "store/types";

import type { NavigationLinkConfig } from "./types";
import { BeneficiaryNavigation, BookNavigation, PublisherNavigation } from "./variants";

/**
 * Navigation links configuration for each portal type
 */
const getNavbarLinks = (
    portalType: EPortalType,
    usesAgreementValidation: boolean,
    isArteCompany: boolean,
    urssaf: boolean,
    usesAgreements: boolean,
): NavigationLinkConfig[] => {
    const configs: Record<EPortalType, NavigationLinkConfig[]> = {
        [EPortalType.BOOK]: [], // Book has its own internal configuration
        [EPortalType.BENEFICIARY]: [
            {
                to: "/dashboard",
                icon: <Dashboard />,
                label: "NavigationBar.Dashboard",
            },
            {
                to: "/titles",
                icon: <Wallet />,
                label: "NavigationBar.Titles",
            },
            {
                to: "/statements",
                icon: <DocumentOutline />,
                label: "NavigationBar.Statements",
            },
            {
                to: "/documents",
                icon: <Folder />,
                label: "NavigationBar.Documents",
            },
        ],
        [EPortalType.PRESS]: [
            {
                to: "/management",
                icon: <Management />,
                label: "NavigationBar.Management",
                canAccess: (ability) => ability.can("access", "ManagementPage"),
            },
            {
                to: "/publications",
                icon: <Titles />,
                label: "NavigationBar.Publications",
                isDisabled: (ability) => !ability.can("access", "PublicationsPage"),
            },
            {
                to: "/beneficiars",
                icon: <Beneficiaries />,
                label: "NavigationBar.Beneficiars",
                isDisabled: (ability) => !ability.can("access", "BeneficiarsPage"),
            },
            {
                to: "/payment",
                icon: <Wallet />,
                label: "NavigationBar.Payment",
                canAccess: (ability) => ability.can("access", "PaymentPage"),
            },
            {
                to: "/declarations",
                icon: <FileSuccess color="currentColor" />,
                label: "NavigationBar.Declarations",
                extraCondition: urssaf,
                canAccess: (ability) => ability.can("access", "DeclarationsPage"),
            },
            {
                to: "/agreements",
                icon: <DrawIcon />,
                label: "NavigationBar.ContractManagement",
                extraCondition: usesAgreementValidation || usesAgreements,
            },
        ],
        [EPortalType.AUDIOVISUAL]: [
            {
                to: "/management",
                icon: <Management />,
                label: "NavigationBar.Management",
                // Arte shows to all users, non-Arte uses standard access check
                canAccess: (ability) => {
                    if (isArteCompany) return true;
                    return ability.can("access", "ManagementPage");
                },
            },
            {
                to: "/publications",
                icon: <Titles />,
                label: "NavigationBar.Productions",
                isDisabled: (ability) => {
                    if (isArteCompany) return false;
                    return !ability.can("access", "PublicationsPage");
                },
            },
            {
                to: "/beneficiars",
                icon: <Beneficiaries />,
                label: "NavigationBar.Beneficiars",
                isDisabled: (ability) => !ability.can("access", "BeneficiarsPage"),
            },
            {
                to: "/payment",
                icon: <Wallet />,
                label: "NavigationBar.Payment",
                canAccess: (ability) => ability.can("access", "PaymentPage"),
            },
            {
                to: "/declarations",
                icon: <FileSuccess color="currentColor" />,
                label: "NavigationBar.Declarations",
                extraCondition: urssaf,
                canAccess: (ability) => ability.can("access", "DeclarationsPage"),
            },
            {
                to: "/agreements",
                icon: <DrawIcon />,
                label: "NavigationBar.ContractManagement",
                extraCondition: usesAgreementValidation || usesAgreements,
            },
        ],
        [EPortalType.URSSAF]: [
            {
                to: "/declarer",
                icon: <FileSuccess color="currentColor" width="20" height="20" />,
                label: "NavigationBar.Declare",
            },
            {
                to: "/analyse",
                icon: <History />,
                label: "NavigationBar.Analyse",
            },
        ],
    };

    return configs[portalType] || [];
};

/**
 * Bottom navigation links (settings) for each portal type
 */
const getBottomLinks = (_portalType: EPortalType): NavigationLinkConfig[] => {
    const settingsLink: NavigationLinkConfig = {
        to: "/settings",
        icon: <Settings />,
        label: "NavigationBar.Settings",
    };

    return [settingsLink];
};

/**
 * Main navigation wrapper component
 * Determines which navigation variant to render based on portal type
 */
const NavigationWrapper = () => {
    const { getCompanySettings, isCompany, countryFeatures } = useCompanySettings();
    const { user } = useAuthenticator((context) => [context.user]);

    const accountType = getCognitoUserAttribute("custom:accountType", user);
    const isArteCompany = isCompany["arte"];
    const usesAgreementValidation = getCompanySettings("uses_agreement_validation_chain");
    const usesAgreements = getCompanySettings("uses_agreements");
    const usesUrssafDeclarations = getCompanySettings("uses_urssaf_declarations") ?? true;
    const showDeclarationsPage = countryFeatures.urssaf && usesUrssafDeclarations;
    const publisherType = useContext(PortalType);
    const navigate = useNavigate();
    const location = useLocation();
    const maintenanceLocked = useAppSelector(
        (state) => state.appMaintenance.unavailable && state.appMaintenance.bypassed,
    );
    const maintenanceSettingsOpen = location.pathname.startsWith("/admin-panel/maintenance");

    useEffect(() => {
        if (!maintenanceLocked || maintenanceSettingsOpen) return;
        navigate("/admin-panel/maintenance", { replace: true });
    }, [maintenanceLocked, maintenanceSettingsOpen, navigate]);

    useEffect(() => {
        document.body.classList.toggle("maintenance-nav-locked", maintenanceLocked);
        return () => document.body.classList.remove("maintenance-nav-locked");
    }, [maintenanceLocked]);

    const navLinks = getNavbarLinks(
        publisherType as EPortalType,
        usesAgreementValidation,
        isArteCompany,
        showDeclarationsPage,
        !!usesAgreements,
    );
    const bottomLinks = getBottomLinks(publisherType as EPortalType);

    // Add admin panel link if user is admin
    if (accountType === "admin") {
        navLinks.push({
            to: "/admin-panel/companiesList",
            icon: <Settings />,
            label: "AdminPanel.AdminPanel",
        });
    }

    const renderNavigation = () => {
        switch (publisherType) {
            case EPortalType.BOOK:
                return <BookNavigation />;

            case EPortalType.BENEFICIARY:
                return <BeneficiaryNavigation />;

            case EPortalType.PRESS:
            case EPortalType.AUDIOVISUAL:
            case EPortalType.URSSAF:
                return <PublisherNavigation navLinks={navLinks} bottomLinks={bottomLinks} />;

            default:
                return <BookNavigation />;
        }
    };

    return (
        <div className="navigation-shell">
            <MaintenanceSoonBanner />
            <div className="navigation-shell-main">
                {renderNavigation()}
                <Outlet />
            </div>
        </div>
    );
};

export default NavigationWrapper;
